// Shrink a machine-generated GLB before it is uploaded to blob storage.
//
// Browser port of ev-world's two-stage pipeline (back-end/tools/glb-optim/
// prep-generated.mjs + services/glb_generated.py), which takes the GPU
// service's ~16 MB output down to well under 1 MB. The generator's output has
// the same defects here as there, because it is the same generator:
//
//   1. NO NORMAL attribute — three.js falls back to flat shading, and worse
//      after simplification. We compute smooth, area-weighted normals.
//   2. metallicFactor 1.0 with a metallic-heavy MR map — without an
//      environment map metals render dark and muddy. Clamped to 0 (the MR
//      texture stays: glTF multiplies factor × channel, so roughness lives).
//   3. Single-sided open shells — the side the photo never showed reads as a
//      hole from behind. Materials become doubleSided.
//   4. Full-budget geometry (~40k triangles) and 2048²/4096² textures —
//      simplified to the quality preset's triangle budget and textures
//      downsized/re-encoded via canvas.
//
// DELIBERATE deviations from ev-world:
//   * No meshopt/Draco compression, no webp, no quantization — output stays
//     PLAIN CORE glTF (jpeg/png images). The student app's loader only ever
//     sees plain GLBs from the cloud pipeline today, and a compressed
//     extension it cannot decode would break it silently. Textures dominate
//     the bytes anyway, so this still captures most of the win.
//   * No real-world scale bake — EV's viewer/app have their own placement
//     conventions and today's cloud models arrive unscaled; changing that
//     invariant here would resize existing content flows.
//
// Every step degrades rather than fails (ev-world's rule): any error returns
// the ORIGINAL bytes — a heavy model beats a lost one. Heavy libraries load
// lazily so the main bundle does not grow.

const MAX_METALNESS = 0;

// High deliberately still reduces (re-encode + mild simplify); mid ≈ ev-world's
// generated preset (20k / 1024²); low is their "strong reduce" territory.
const QUALITY_PRESETS = {
  high: { triangleBudget: 30000, textureSize: 2048, jpegQuality: 0.9, simplifyError: 0.001 },
  mid: { triangleBudget: 18000, textureSize: 1024, jpegQuality: 0.85, simplifyError: 0.001 },
  low: { triangleBudget: 8000, textureSize: 512, jpegQuality: 0.8, simplifyError: 0.0025 },
};

const GLB_MAGIC = 0x46546c67; // 'glTF' little-endian

/** Reject truncated or non-glTF payloads (port of ev-world validate_glb):
 *  the 12-byte header declares the total length, which catches a cut-short
 *  download that still begins with the right magic. */
export function validateGlbBytes(bytes) {
  if (!bytes || bytes.byteLength < 12) {
    throw new Error("The model file is too small to be a glTF container.");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, true) !== GLB_MAGIC) {
    throw new Error("The downloaded file is not a binary glTF (.glb).");
  }
  const declared = view.getUint32(8, true);
  if (declared !== bytes.byteLength) {
    throw new Error(
      "The model download was incomplete (" + bytes.byteLength + " bytes received, " + declared + " declared)."
    );
  }
}

/** Smooth normals, accumulated per unique POSITION and area-weighted — a
 * direct port of ev-world's prep-generated.mjs. Not functions' normals():
 * that unwelds and writes FLAT face normals, baking the faceting in; per-index
 * accumulation would seam at every UV split of the generated texture atlas. */
function addSmoothNormals(document) {
  let touched = 0;
  for (const mesh of document.getRoot().listMeshes()) {
    for (const prim of mesh.listPrimitives()) {
      if (prim.getAttribute("NORMAL")) continue;
      const position = prim.getAttribute("POSITION");
      const indices = prim.getIndices();
      if (!position) continue;

      const count = position.getCount();
      const normals = new Float32Array(count * 3);

      const canonical = new Uint32Array(count);
      const byPosition = new Map();
      const p = [0, 0, 0];
      for (let i = 0; i < count; i++) {
        position.getElement(i, p);
        // 5 decimals: tight enough not to weld distinct geometry, loose
        // enough to catch float error between duplicated seam vertices.
        const key = p[0].toFixed(5) + "," + p[1].toFixed(5) + "," + p[2].toFixed(5);
        const existing = byPosition.get(key);
        if (existing === undefined) {
          byPosition.set(key, i);
          canonical[i] = i;
        } else {
          canonical[i] = existing;
        }
      }

      const triangleCount = indices ? indices.getCount() / 3 : count / 3;
      const a = [0, 0, 0], b = [0, 0, 0], c = [0, 0, 0];
      for (let t = 0; t < triangleCount; t++) {
        const i0 = indices ? indices.getScalar(t * 3) : t * 3;
        const i1 = indices ? indices.getScalar(t * 3 + 1) : t * 3 + 1;
        const i2 = indices ? indices.getScalar(t * 3 + 2) : t * 3 + 2;
        position.getElement(i0, a);
        position.getElement(i1, b);
        position.getElement(i2, c);

        const e1x = b[0] - a[0], e1y = b[1] - a[1], e1z = b[2] - a[2];
        const e2x = c[0] - a[0], e2y = c[1] - a[1], e2z = c[2] - a[2];
        // Unnormalised cross product == area weighting.
        const nx = e1y * e2z - e1z * e2y;
        const ny = e1z * e2x - e1x * e2z;
        const nz = e1x * e2y - e1y * e2x;

        const tri = [i0, i1, i2];
        for (let j = 0; j < 3; j++) {
          const k = canonical[tri[j]] * 3;
          normals[k] += nx;
          normals[k + 1] += ny;
          normals[k + 2] += nz;
        }
      }

      for (let i = 0; i < count; i++) {
        const k = canonical[i] * 3;
        let nx = normals[k], ny = normals[k + 1], nz = normals[k + 2];
        const len = Math.hypot(nx, ny, nz);
        if (len > 0) {
          nx /= len; ny /= len; nz /= len;
        } else {
          nx = 0; ny = 1; nz = 0; // degenerate vertex: stable direction, not NaN
        }
        const j = i * 3;
        normals[j] = nx; normals[j + 1] = ny; normals[j + 2] = nz;
      }

      const buffer = document.getRoot().listBuffers()[0] || document.createBuffer();
      const accessor = document.createAccessor().setType("VEC3").setArray(normals).setBuffer(buffer);
      prim.setAttribute("NORMAL", accessor);
      touched++;
    }
  }
  return touched;
}

function countTriangles(document) {
  let triangles = 0;
  for (const mesh of document.getRoot().listMeshes()) {
    for (const prim of mesh.listPrimitives()) {
      const indices = prim.getIndices();
      const position = prim.getAttribute("POSITION");
      const count = indices ? indices.getCount() : (position ? position.getCount() : 0);
      triangles += Math.floor(count / 3);
    }
  }
  return triangles;
}

/** Does the (png) image actually use its alpha channel? Probed on a 32×32
 * downsample so a 2048² texture never gets a full-resolution scan. */
function probeHasAlpha(bitmap) {
  try {
    const canvas = typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(32, 32)
      : Object.assign(window.document.createElement("canvas"), { width: 32, height: 32 });
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0, 32, 32);
    const data = ctx.getImageData(0, 0, 32, 32).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) return true;
    }
  } catch (e) {
    return true; // cannot tell — keep png, the safe choice
  }
  return false;
}

async function canvasEncode(bitmap, width, height, mime, quality) {
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(width, height);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const blob = await canvas.convertToBlob({ type: mime, quality });
    return new Uint8Array(await blob.arrayBuffer());
  }
  const canvas = window.document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
  if (!blob) return null;
  return new Uint8Array(await blob.arrayBuffer());
}

/** Downsize + re-encode every texture via canvas. Core-glTF output only:
 * jpeg for opaque images, png when alpha is genuinely used. Returns how many
 * textures were replaced. Skips silently where canvas APIs are missing. */
async function recompressTextures(document, maxSize, jpegQuality) {
  if (typeof createImageBitmap !== "function") return 0;
  let replaced = 0;
  for (const texture of document.getRoot().listTextures()) {
    const bytes = texture.getImage();
    const mime = texture.getMimeType() || "image/png";
    if (!bytes || !/^image\/(png|jpeg)$/.test(mime)) continue; // never touch webp/ktx2 blindly

    let bitmap;
    try {
      bitmap = await createImageBitmap(new Blob([bytes], { type: mime }));
    } catch (e) {
      continue; // undecodable in this browser — leave as-is
    }
    try {
      const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const keepAlpha = mime === "image/png" && probeHasAlpha(bitmap);
      const outMime = keepAlpha ? "image/png" : "image/jpeg";
      const out = await canvasEncode(bitmap, w, h, outMime, jpegQuality);
      // Only take a replacement that is actually a win.
      if (out && out.byteLength > 0 && (out.byteLength < bytes.byteLength || scale < 1)) {
        texture.setImage(out).setMimeType(outMime);
        replaced++;
      }
    } finally {
      if (bitmap.close) bitmap.close();
    }
  }
  return replaced;
}

/**
 * Optimize a generated GLB for the given quality ("high" | "mid" | "low").
 * Input and output are Blobs. Resolves { blob, stats }; on ANY failure the
 * original blob comes back with stats.stage === "raw".
 */
export async function optimizeGeneratedGlb(blob, quality) {
  const preset = QUALITY_PRESETS[quality] || QUALITY_PRESETS.high;
  const sourceBytes = blob.size;
  const stats = {
    stage: "raw", quality: QUALITY_PRESETS[quality] ? quality : "high",
    sourceBytes, outputBytes: sourceBytes,
    triangles: null, outputTriangles: null, normalsAdded: 0, texturesRecompressed: 0,
  };

  try {
    const input = new Uint8Array(await blob.arrayBuffer());
    validateGlbBytes(input);

    // Heavy deps load on first use only (code-split away from the main bundle).
    const [core, extensions, functions, meshopt] = await Promise.all([
      import("@gltf-transform/core"),
      import("@gltf-transform/extensions"),
      import("@gltf-transform/functions"),
      import("meshoptimizer"),
    ]);
    const io = new core.WebIO().registerExtensions(extensions.ALL_EXTENSIONS);
    const document = await io.readBinary(input);

    // ── prep: normals, metalness clamp, double-sided ────────────────────────
    stats.normalsAdded = addSmoothNormals(document);
    for (const material of document.getRoot().listMaterials()) {
      if (material.getMetallicFactor() > MAX_METALNESS) material.setMetallicFactor(MAX_METALNESS);
      if (!material.getDoubleSided()) material.setDoubleSided(true);
    }

    // ── geometry: weld, simplify to the ABSOLUTE triangle budget, tidy ──────
    // Ratio derived from the budget (ev-world's rule): the generator's fixed
    // 40k output is a bet we refuse to hard-code.
    stats.triangles = countTriangles(document);
    const transforms = [functions.weld()];
    if (stats.triangles > preset.triangleBudget) {
      const simplifier = meshopt.MeshoptSimplifier;
      await simplifier.ready;
      const ratio = Math.min(1, Math.max(0.05, preset.triangleBudget / stats.triangles));
      transforms.push(functions.simplify({ simplifier, ratio, error: preset.simplifyError }));
    }
    transforms.push(functions.prune(), functions.dedup());
    await document.transform(...transforms);
    stats.outputTriangles = countTriangles(document);

    // ── textures: downsize + re-encode (the big win) ────────────────────────
    stats.texturesRecompressed = await recompressTextures(document, preset.textureSize, preset.jpegQuality);

    const output = await io.writeBinary(document);
    validateGlbBytes(output);

    // Accept when smaller, or when the semantic fixes ran (normals are worth
    // a few extra KB — a flat-shaded model is the worse outcome).
    if (output.byteLength < sourceBytes || stats.normalsAdded > 0) {
      stats.stage = "optimized";
      stats.outputBytes = output.byteLength;
      return { blob: new Blob([output], { type: "model/gltf-binary" }), stats };
    }
    return { blob, stats };
  } catch (e) {
    console.warn("[3D] GLB optimization failed; storing the model as-is", e);
    return { blob, stats };
  }
}

const GlbOptimizer = { optimizeGeneratedGlb, validateGlbBytes, QUALITY_PRESETS };
export default GlbOptimizer;
